import { cva, type VariantProps } from 'class-variance-authority'
import {
    CircleAlertIcon,
    CircleCheckIcon,
    InfoIcon,
    TriangleAlertIcon,
    XIcon,
} from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'
import IconButton from './Button/IconButton'

const alertVariants = cva(
    'relative flex w-full items-start gap-3 rounded-lg px-4 py-3 text-sm text-foreground [&_svg]:size-5 [&_svg]:shrink-0',
    {
        variants: {
            severity: {
                success: '',
                info: '',
                warning: '',
                error: '',
            },
            variant: {
                standard: '',
                filled: 'text-black [&_svg]:text-black',
                outlined: 'border bg-transparent',
            },
        },
        compoundVariants: [
            {
                variant: 'standard',
                severity: 'success',
                className: 'bg-success/12 [&_svg]:text-success',
            },
            {
                variant: 'standard',
                severity: 'info',
                className: 'bg-info/12 [&_svg]:text-info',
            },
            {
                variant: 'standard',
                severity: 'warning',
                className: 'bg-warning/12 [&_svg]:text-warning',
            },
            {
                variant: 'standard',
                severity: 'error',
                className: 'bg-error/12 [&_svg]:text-error',
            },
            // filled
            { variant: 'filled', severity: 'success', className: 'bg-success' },
            { variant: 'filled', severity: 'info', className: 'bg-info' },
            {
                variant: 'filled',
                severity: 'warning',
                className: 'bg-warning',
            },
            { variant: 'filled', severity: 'error', className: 'bg-error' },
            // outlined
            {
                variant: 'outlined',
                severity: 'success',
                className: 'border-success [&_svg]:text-success',
            },
            {
                variant: 'outlined',
                severity: 'info',
                className: 'border-info [&_svg]:text-info',
            },
            {
                variant: 'outlined',
                severity: 'warning',
                className: 'border-warning [&_svg]:text-warning',
            },
            {
                variant: 'outlined',
                severity: 'error',
                className: 'border-error [&_svg]:text-error',
            },
        ],
        defaultVariants: {
            severity: 'success',
            variant: 'standard',
        },
    },
)

const defaultIcons = {
    success: CircleCheckIcon,
    info: InfoIcon,
    warning: TriangleAlertIcon,
    error: CircleAlertIcon,
}

type AlertProps = Omit<React.ComponentProps<'div'>, 'title'> &
    VariantProps<typeof alertVariants> & {
        /** Ícone customizado. Passe `false` para ocultar o ícone. */
        icon?: React.ReactNode | false
        /** Conteúdo renderizado à direita (ex.: botão de ação). */
        action?: React.ReactNode
        /** Quando definido, renderiza um botão de fechar. */
        onClose?: () => void
    }

function Alert({
    className,
    severity = 'success',
    variant = 'standard',
    icon,
    action,
    onClose,
    children,
    ...props
}: AlertProps) {
    const DefaultIcon = defaultIcons[severity ?? 'success']

    return (
        <div
            role="alert"
            data-slot="alert"
            data-severity={severity}
            className={cn(alertVariants({ severity, variant }), className)}
            {...props}
        >
            {icon !== false && (
                <span data-slot="alert-icon" className="flex py-0.5 my-auto">
                    {icon ?? <DefaultIcon />}
                </span>
            )}

            <div
                data-slot="alert-content"
                className="min-w-0 flex-1 self-center py-0.5"
            >
                {children}
            </div>

            {action && (
                <span
                    data-slot="alert-action"
                    className="-mr-1 ml-auto self-start"
                >
                    {action}
                </span>
            )}

            {onClose && (
                <IconButton
                    aria-label="Fechar"
                    onClick={onClose}
                    title="Fechar aviso"
                >
                    <XIcon className="text-primary-foreground" />
                </IconButton>
            )}
        </div>
    )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="alert-title"
            className={cn('mb-1 font-semibold leading-5', className)}
            {...props}
        />
    )
}

export { Alert, AlertTitle, alertVariants }
